import { useState } from 'react';
import { Mail, Trash2, UserCheck, UserX } from 'lucide-react';
import api from '../../../utils/api';
import { apiErrorMessage } from '../../../utils/apiError';
import { EnterpriseEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { formatLagos } from '../../../utils/datetime';
import { formatCount } from '../../../utils/money';
import { useAuth } from '../../../AuthContext';
import { useServerTable, wholeDayDates } from '../../../hooks/useServerTable';
import PageHeader from '../../shell/PageHeader';
import {
  Alert,
  Button,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  IconButton,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import InviteUserModal from '../modals/InviteUserModal';
import ConfirmActionModal from '../modals/ConfirmActionModal';
import EnterpriseDeactivated, { isEnterpriseDeactivated } from './enterprise/EnterpriseDeactivated';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Users' }];
const USER_STATUSES = ['active', 'deactivated'];
const INVITE_NOTE =
  'We email them a temporary password. They sign in as a viewer: they can see every page but cannot pay out or manage users.';

// CashCard staff who invite a user are never identified; the dashboard is branded CashAfrica.
const inviterName = (invitedBy) => {
  if (!invitedBy) return '—';
  return invitedBy.type === 'cashcard' ? 'CashAfrica' : invitedBy.name;
};

const EnterpriseUsers = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(Permissions.enterpriseUsersManage);

  const table = useServerTable(EnterpriseEndpoints.users, {
    filters: { status: '' },
    dateParams: wholeDayDates,
    fallbackError: 'We could not load the users. Please try again.',
  });

  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  if (isEnterpriseDeactivated(table.requestError)) {
    return (
      <>
        <PageHeader title="Users" breadcrumbs={breadcrumbs} />
        <EnterpriseDeactivated />
      </>
    );
  }

  const handleInvite = async (payload) => {
    await api.post(EnterpriseEndpoints.users, payload);
    setNotice(`An invitation email has been sent to ${payload.email}.`);
    table.setPage(0);
    table.refetch();
  };

  const changeStatus = async (user, status) => {
    await api.patch(EnterpriseEndpoints.userStatus(user.id), { status });
    setNotice(
      status === 'deactivated' ? `${user.full_name} has been deactivated.` : `${user.full_name} has been reactivated.`
    );
    table.refetch();
  };

  const handleActivate = async (user) => {
    setActionError('');
    setActivatingId(user.id);
    try {
      await changeStatus(user, 'active');
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not activate this user.'));
    } finally {
      setActivatingId(null);
    }
  };

  const handleResend = async () => {
    const { data } = await api.post(EnterpriseEndpoints.userResendInvite(resendTarget.id), {});
    setNotice(data?.message || 'A new password has been emailed to the user.');
  };

  const handleDelete = async () => {
    await api.delete(EnterpriseEndpoints.user(deleteTarget.id));
    setNotice(`${deleteTarget.full_name} has been removed.`);
    table.refetch();
  };

  const columns = [
    {
      key: 'full_name',
      header: 'Name',
      sortKey: 'first_name',
      render: (user) => (
        <span className="whitespace-nowrap">
          {user.full_name}
          {user.is_owner && <span className="badge bg-primary-500/10 text-primary-600 ml-2">Owner</span>}
        </span>
      ),
    },
    { key: 'email', header: 'Email' },
    { key: 'phone_number', header: 'Phone' },
    {
      key: 'role',
      header: 'Role',
      render: (user) => <span className="badge bg-secondary-500/10 text-secondary-700">{user.role?.label}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (user) => <StatusPill status={user.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={USER_STATUSES}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    {
      key: 'invited_by',
      header: 'Invited by',
      render: (user) => <span className="whitespace-nowrap">{inviterName(user.invited_by)}</span>,
    },
    {
      key: 'invited_at',
      header: 'Invited on (WAT)',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (user) => <span className="whitespace-nowrap">{formatLagos(user.invited_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Invited"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
  ];

  if (canManage) {
    columns.push({
      key: 'actions',
      header: 'Actions',
      align: 'center',
      render: (user) =>
        !user.is_owner && (
          <div className="flex items-center justify-center gap-1">
            <IconButton
              icon={Mail}
              label="Resend invite"
              tooltip="Email a new password and sign them out"
              onClick={() => setResendTarget(user)}
            />
            {user.status === 'deactivated' ? (
              <IconButton
                icon={UserCheck}
                label="Activate"
                tooltip="Restore this user's access"
                variant="link-success"
                disabled={activatingId === user.id}
                onClick={() => handleActivate(user)}
              />
            ) : (
              <IconButton
                icon={UserX}
                label="Deactivate"
                tooltip="Revoke this user's access"
                variant="link-danger"
                onClick={() => setDeactivateTarget(user)}
              />
            )}
            <IconButton
              icon={Trash2}
              label="Delete"
              tooltip="Remove this user"
              variant="link-danger"
              onClick={() => setDeleteTarget(user)}
            />
          </div>
        ),
    });
  }

  return (
    <>
      <PageHeader
        title="Users"
        breadcrumbs={breadcrumbs}
        actions={canManage && <Button onClick={() => setInviteOpen(true)}>Invite a user</Button>}
      />

      <TableCard
        title="Users"
        summary={{ label: 'Total', value: formatCount(table.total), loading: table.loading }}
        toolbar={<SearchInput value={table.search} onChange={table.setSearch} placeholder="Search name or email" />}
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        {(notice || actionError) && (
          <div className="flex flex-col gap-3 p-4 pb-0">
            {notice && (
              <Alert variant="success" onDismiss={() => setNotice('')}>
                {notice}
              </Alert>
            )}
            {actionError && (
              <Alert variant="danger" onDismiss={() => setActionError('')}>
                {actionError}
              </Alert>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(user) => user.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No users', message: 'No users match these filters.' }}
        />
      </TableCard>

      <InviteUserModal
        isOpen={inviteOpen}
        note={INVITE_NOTE}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
      />

      <ConfirmActionModal
        isOpen={Boolean(resendTarget)}
        title="Resend invite and sign them out"
        message={
          resendTarget
            ? `This generates a new password, emails it to ${resendTarget.email} and signs them out everywhere. Their current password stops working.`
            : ''
        }
        confirmLabel="Resend and sign out"
        onClose={() => setResendTarget(null)}
        onConfirm={handleResend}
      />

      <ConfirmActionModal
        isOpen={Boolean(deactivateTarget)}
        title="Deactivate user"
        message={
          deactivateTarget
            ? `This signs ${deactivateTarget.full_name} out and blocks them from signing in. It is reversible.`
            : ''
        }
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => changeStatus(deactivateTarget, 'deactivated')}
      />

      <ConfirmActionModal
        isOpen={Boolean(deleteTarget)}
        title="Delete user"
        message={
          deleteTarget
            ? `This signs ${deleteTarget.full_name} out and removes their access permanently. Their email and phone number cannot be invited again.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default EnterpriseUsers;
